// Lightweight cart context — persisted to localStorage so it survives reloads.
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const STORAGE_KEY = "nc_cart_v1";
const CartContext = createContext(null);

const itemKey = (it) => `${it.plan_id}::${it.file_type}`;

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const addItem = useCallback((item) => {
    // item = {plan_id, file_type, plan_title, plan_slug, price_paise}
    setItems(prev => {
      const k = itemKey(item);
      if (prev.some(p => itemKey(p) === k)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((plan_id, file_type) => {
    setItems(prev => prev.filter(p => itemKey(p) !== itemKey({ plan_id, file_type })));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const has = useCallback((plan_id, file_type) =>
    items.some(p => itemKey(p) === itemKey({ plan_id, file_type })), [items]);

  const subtotalPaise = useMemo(
    () => items.reduce((s, it) => s + (Number(it.price_paise) || 0), 0),
    [items]
  );

  const value = useMemo(() => ({
    items, addItem, removeItem, clear, has, count: items.length, subtotalPaise,
  }), [items, addItem, removeItem, clear, has, subtotalPaise]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

export function formatINR(paise) {
  const rupees = (Number(paise) || 0) / 100;
  return "₹" + rupees.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
